import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Checkbox } from "../../../common/components/Input";
import { copyToClipboard } from "../../../common/utils/clipboard";
import { getMfaRecoveryCodes } from "../state/mfaSlice";

interface RecoveryCodesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const RecoveryCodesDialog = ({ isOpen, onClose }: RecoveryCodesDialogProps) => {
  const { t } = useTranslation();
  const codes = useAppSelector(getMfaRecoveryCodes);

  const [hasConfirmedSaved, setHasConfirmedSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setHasConfirmedSaved(false);
    setCopied(false);
  }, [codes]);

  const codesText = (codes ?? []).join("\n");

  const handleCopy = async () => {
    if (!codes || codes.length === 0) return;
    await copyToClipboard(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!codes || codes.length === 0) return;
    const blob = new Blob([`${codesText}\n`], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "wixar-recovery-codes.txt");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <ModalStandard
      id="modalMfaRecoveryCodes"
      fullScreen={false}
      controlledMode={{ isOpen }}
      clickOnBackgroundClose={false}
    >
      <div className="flex w-full">
        <div className="relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto">
          <h2 className="pt-3 pb-1 font-medium text-lg">
            {t("pages.profile.mfa.recoveryCodes.title")}
          </h2>
          <p className="pb-3 text-red-700 font-medium">
            {t("pages.profile.mfa.recoveryCodes.shownOnceWarning")}
          </p>

          <div className="grid grid-cols-2 gap-2 bg-white rounded-lg border border-gray-300 p-4 font-mono text-sm">
            {(codes ?? []).map((code) => (
              <span key={code} className="select-all">
                {code}
              </span>
            ))}
          </div>

          <div className="flex gap-2 mt-3">
            <button type="button" className="btn-alternative-outline" onClick={handleCopy}>
              {copied ? t("pages.profile.mfa.recoveryCodes.copied") : t("general.copy")}
            </button>
            <button type="button" className="btn-alternative-outline" onClick={handleDownload}>
              {t("general.download")}
            </button>
          </div>

          <div className="mt-2">
            <Checkbox
              id="mfa-recovery-codes-saved"
              label={t("pages.profile.mfa.recoveryCodes.confirmCheckbox")}
              checked={hasConfirmedSaved}
              onChange={(e) => setHasConfirmedSaved(e.target.checked)}
            />
          </div>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="btn-primary-fill"
              disabled={!hasConfirmedSaved}
              onClick={onClose}
            >
              {t("general.close")}
            </button>
          </div>
        </div>
      </div>
    </ModalStandard>
  );
};

export default RecoveryCodesDialog;
